import { Component, OnDestroy, Signal, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { IntervalCount, SportIntervalCounts } from '@frontend/sports/calendar/data-access';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { DeviceSizeService } from '@frontend/sports/common/core/utils/media-query';
import { ListItemComponent } from '@frontend/sports/common/ui/sports-list-items';
import { SportUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { TrackReferringACtionDirective } from '@frontend/sports/tracking/feature';
import { DsDivider } from '@frontend/ui/divider';
import { keys, now, orderBy } from 'lodash-es';
import { Observable } from 'rxjs';

import { ErrorComponent } from '../common/error/error.component';
import { LiveNowLinkRegisterService } from '../live-now-item/live-now-link-register.service';

export interface CalendarLink extends IntervalCount {
    url: string;
}

@HooksWireup()
@Component({
    selector: 'ms-calendar',
    templateUrl: 'calendar.component.html',
    imports: [ErrorComponent, ListItemComponent, DsDivider, TrackReferringACtionDirective],
})
export class CalendarComponent extends OnRouteResolve<SportIntervalCounts> implements OnDestroy {
    links: CalendarLink[];
    isEpcot: boolean;
    mediaQuery$: Observable<boolean>;
    isMobile: Signal<boolean | undefined>;
    trackingValue = signal<string>('M2_ListItemlinks');
    cleanedAfterTrack = signal(false);

    constructor(
        public sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        route: ActivatedRoute,
        private liveButton: LiveNowLinkRegisterService,
        private epcotConfig: EpcotConfig,
        private deviceSizeService: DeviceSizeService,
    ) {
        super(route);
        this.isEpcot = this.epcotConfig.isEnabled;
        this.isMobile = this.deviceSizeService.isMobile;
    }

    onRouteResolved(counts: SportIntervalCounts): void {
        this.links = this.getLinks(counts);
        this.liveButton.addLiveButton(counts.sport);
    }

    ngOnDestroy(): void {
        this.liveButton.removeLiveButton();
    }

    showBorder(isLastItem: boolean, index: number): boolean {
        if (this.isMobile()) {
            return !isLastItem;
        }
        return !(isLastItem || index === this.links.length - 2);
    }

    onSelected(selectedLink: CalendarLink) {
        this.cleanedAfterTrack.update(() => selectedLink.url?.indexOf('http') === 0 || false);
    }

    private getLinks(counts: SportIntervalCounts): CalendarLink[] {
        const time = now();
        const links = keys(counts.intervals)
            .map((key) => this.getUrl(key, counts.intervals[key]!, counts.sport))
            .filter(isDefined);

        return orderBy(
            links,
            [
                (link) => (!link.from || link.from.getTime() < time ? time : link.from),
                (link) => (!link.to || link.to.getTime() < time ? time : link.to),
            ],
            ['asc', 'asc'],
        );
    }

    private getUrl(key: string, interval: IntervalCount, sport: CountItem | undefined): CalendarLink | undefined {
        let url: string;

        if (key === 'all') {
            return;
        } else if (key === 'live') {
            if (sport) {
                url = this.urlHelper.getLiveSportUrl({ sport, isEsport: sport.isEsport });
            } else {
                url = this.urlHelper.getLiveUrl();
            }
        } else {
            if (sport) {
                url = this.urlHelper.getSportUrl(sport, interval.name, SportUrlParam.Competitions);
            } else {
                url = this.urlHelper.getCalendarUrl(interval.name);
            }
        }

        return { ...interval, url };
    }
}
